// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { isServer, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createIDBStorage } from "../storage/query-storage";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
        // We have to set a gc time equal to the persister max age. See
        // See https://tanstack.com/query/latest/docs/framework/react/plugins/persistQueryClient#how-it-works
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

/**
 * Persistence allow-list for the IndexedDB cache.
 *
 * Architectural direction: TanStack Query persistence is only for queries
 * whose resources are NOT on the offline-first system. Every Dexie-backed
 * table (pre-orders, flows, partners, products, units, locations, stocks,
 * prices, lots, emails, WhatsApp/SMS chats+messages, AI workflows /
 * executions / chat-sessions / chat-messages, proposal batches, feedback)
 * is already persisted by Dexie itself — running it through the TS Query
 * persister means we re-serialise + re-write the same data to IndexedDB
 * via a second, slower path. The CPU profile showed `dehydrate` + `trySave`
 * dominating during a recap pre-order open; this gate stops it at the door.
 *
 * Allow-listed roots are identity / config / capability queries with no
 * Dexie equivalent that genuinely benefit from cross-session warm-up:
 *
 * - "company" / "companies" — current company + memberships
 * - "user" / "users" — current viewer + user listings
 * - "feature-flags" — gating flags, read by useFeatureFlag everywhere
 *
 * Everything else is denied. Dexie-backed reads survive a reload via
 * SyncEngine + Dexie. Genuinely-network reads (reports, dashboards,
 * agent-executions, fees, partner preferences, etc.) re-fetch on demand —
 * persisting their stale state across sessions is rarely correct anyway.
 */
const PERSIST_ALLOW_LIST: ReadonlySet<string> = new Set([
  "company",
  "companies",
  "user",
  "users",
  "feature-flags",
]);

function isPersistableQuery(key: unknown): boolean {
  if (!Array.isArray(key)) return false;
  const root = key[0];
  return typeof root === "string" && PERSIST_ALLOW_LIST.has(root);
}

const persister = createAsyncStoragePersister({
  storage: createIDBStorage(),
  // Throttle persistence so a burst of `setQueryData` calls (chat click,
  // recap delta refetch, mutation cascades) collapses into a single
  // IndexedDB write instead of one per cache update. The default 1 s is
  // not enough — chat opens fire ~5 cache writes back-to-back, each
  // re-serializing the whole cache (~1.2 s of CPU in the click trace).
  throttleTime: 5000,
});

export default function QueryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <PersistQueryClientProvider
      client={queryClient}
      onSuccess={() => {
        // After hydrating from IndexedDB, drop any stale unread-count entries
        // that may have been persisted by older builds (this query has
        // staleTime:Infinity + a queryFn returning 0, so the cached value is
        // never refreshed naturally — a stale `1` from a deleted pre-order
        // would otherwise outlive the cleanup until the chat list response
        // races in to overwrite it).
        queryClient.removeQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === "pre-orders" &&
            query.queryKey[1] === "unread-count",
        });
      }}
      persistOptions={{
        persister,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            isPersistableQuery(query.queryKey) &&
            query.state.status === "success",
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
